import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button, Input, Select, Card } from '../components/UI';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';
import { Link, useNavigate } from 'react-router-dom';

const InterestForm = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    region: '',
    role: 'technician',
    preferredLanguage: 'es',
    message: '',
    wantsUpdates: true,
  });

  const [submitted, setSubmitted] = useState(false);

  const validateChileanPhone = (phone) => {
    // Remove all spaces and hyphens
    const cleaned = phone.replace(/[\s-]/g, '');
    
    // Accept formats: +56 9 #### ####, +569########, 9#### ####
    const patterns = [
      /^\+569\d{8}$/,  // +569########
      /^9\d{8}$/,        // 9########
      /^\+56\s?9\d{8}$/ // +56 9########
    ];
    
    return patterns.some(pattern => pattern.test(cleaned));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate Chilean phone format
    if (!validateChileanPhone(formData.phone)) {
      alert(t('invalidPhoneError'));
      return;
    }
    
    // Save to localStorage
    const submissions = JSON.parse(localStorage.getItem('interest_submissions') || '[]');
    submissions.push({
      ...formData,
      submittedAt: new Date().toISOString(),
      language: language,
    });
    localStorage.setItem('interest_submissions', JSON.stringify(submissions));
    
    setSubmitted(true);
  };

  const handleCloseSuccess = () => {
    // Reset form
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      region: '',
      role: 'technician',
      preferredLanguage: 'es',
      message: '',
      wantsUpdates: true,
    });
    setSubmitted(false);
    // Redirect to thank you page
    navigate('/gracias');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <SharedHeader />

      {/* Form Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white text-gray-900">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">{t('interestFormTitle')}</h1>
              <p className="text-gray-600">{t('interestFormSubtitle')}</p>
            </div>

            {submitted ? (
              <div className="text-center py-12 relative">
                <button
                  onClick={handleCloseSuccess}
                  className="absolute top-0 right-0 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-green-600 mb-2">{t('thankYou')}</h2>
                <p className="text-gray-600">{t('willContact')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label={t('fullName')}
                  value={formData.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  placeholder={t('enterFullName')}
                  required
                />

                <Input
                  label={t('email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                />

                <Input
                  label={t('phoneWhatsapp')}
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+56 9 1234 5678"
                  required
                />

                <Input
                  label={t('regionComuna')}
                  value={formData.region}
                  onChange={(e) => handleChange('region', e.target.value)}
                  placeholder={t('enterRegion')}
                  required
                />

                <Select
                  label={t('yourRole')}
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  options={[
                    { value: 'technician', label: t('vetTechnician') },
                    { value: 'technician_uncertified', label: t('vetTechnicianUncertified') },
                    { value: 'clinic', label: t('vetClinic') },
                    { value: 'veterinarian', label: t('veterinarian') },
                    { value: 'pet_sitter', label: t('petSitter') },
                  ]}
                />

                <Select
                  label={t('preferredLanguage')}
                  value={formData.preferredLanguage}
                  onChange={(e) => handleChange('preferredLanguage', e.target.value)}
                  options={[
                    { value: 'es', label: 'Español' },
                    { value: 'en', label: 'English' },
                    { value: 'pt', label: 'Português' },
                  ]}
                />

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('optionalMessage')}
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder={t('messagePlaceholder')}
                  />
                </div>

                <div className="flex items-start mb-4">
                  <input
                    type="checkbox"
                    checked={formData.wantsUpdates}
                    onChange={(e) => handleChange('wantsUpdates', e.target.checked)}
                    className="mt-1 mr-2"
                  />
                  <label className="text-sm text-gray-700">
                    {t('wantLaunchInfo')}
                  </label>
                </div>

                <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                  {t('submitInterest')}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
};

export default InterestForm;
